/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { useStyles } from '~src/hooks/useStyles';
import { styleFn } from './OfferListCard.styles';
import { useIntl } from 'react-intl';
import { Button, message, Tooltip } from 'antd';
import React, {
  Fragment,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import IconButton from '~components/_common/IconButton/IconButton';
import { UploadFileModal } from '~components/molecules/Tabs/TenderTabs/components/ContractAndOffers/ContractBuilder/ContractBuilderGroupCard/UploadFileModal/UploadFileModal';
import { PaperClipOutlined } from '@ant-design/icons';
import dataContextMenu, {
  DataContextMenuItemType,
} from '~src/data/dataContextMenu';
import { BaseRightClickOverlay } from '~components/atoms/RightClickOverlay/BaseRightClickOverlay';
import { useDraggableCard } from '~components/molecules/Tabs/TenderTabs/components/ContractAndOffers/ContractBuilder/ContractBuilderDnD/useDraggableCard';
import FileProgressContext, {
  FileProgress,
} from '~src/contexts/FileProgressContext';
import { UploadFile } from 'antd/es/upload/interface';
import { v4 as uuid } from 'uuid';
import { queryCache, useMutation } from 'react-query';
import {
  deleteVoucherAttachment,
  getVoucherAttachmentDownloadUrl,
} from '~src/services/api/contracts';
import { SimpleConfirmModal } from '~components/popups-modals/ConfirmModal/SimpleConfirmModal';
import moment from 'moment/moment';
import {
  deleteVendorVoucherAttachment,
  getProjectVoucherAttachmentDownloadUrl,
  getVendorVoucherAttachmentDownloadUrl,
} from '~src/services/api';
import {
  renderOptionalVoucherBadge,
  renderVoucherTypeIcon,
} from '~components/molecules/Tabs/TenderTabs/components/ContractAndOffers/utils';
import Viewer from '~components/atoms/Viewers/Viewer';

export interface OfferListCardProps {
  id: string;
  type: string;
  subType: number;
  cardTitle?: string;
  cardSubtitle?: string;
  cardQuantity?: number;
  cardPrice?: number;
  cardSum?: number;

  cardFiles: FileCardType[]; // (Project side files)
  vendorFiles?: FileCardType[]; // (Vendor side files)

  borderless?: boolean;
  contractData: any;
  onContextMenu?: (e: React.MouseEvent) => void;
  onCardAction?: (action: string) => void;
  groupIndex?: number;
  onRightClick?: (e: React.MouseEvent) => void;
  setDraggingCardId?: (id: string | null) => void;
  isDragging?: boolean;
  isOnMyTender: boolean;
  tenderClosed?: boolean;
  isOfferLocked?: boolean;
  cardCreationDate: number;
  responseData?: {
    voucher_id: number;
    sum: number | null;
    is_approved: boolean | null;
  } | null;
  contractDataMyTender: {
    shareId: string;
    shareKey: string;
    contractOfferData: {
      is_offer_created: boolean;
      offer_id: number;
      state: number;
      contract_id: number;
    };
  };
  isDeleted?: boolean;
  contractDeleted?: boolean;
  isOptional?: boolean;
}

type FileCardType = {
  id: string;
  name: string;
  description: string;
  file: File;
  size: number;
  serial_number: number;
  version: number;
  version_at: number;
  type?: string;
};

const OfferListCard: React.FC<OfferListCardProps> = ({
  id,
  type,
  subType,
  cardTitle,
  cardSubtitle,
  cardPrice,
  cardSum,
  borderless,
  cardFiles,
  vendorFiles,
  contractData,
  onContextMenu,
  onCardAction,
  onRightClick,
  setDraggingCardId,
  isDragging,
  isOnMyTender,
  tenderClosed,
  isOfferLocked,
  cardCreationDate,
  responseData,
  contractDataMyTender,
  isDeleted,
  contractDeleted,
  isOptional,
}) => {
  const intl = useIntl();
  const styles = useStyles(styleFn);

  const { projectId, tenderId, contractId } = contractData;

  const [showUploadModal, setShowUploadModal] = useState(false);

  // Project-side attachments (voucher.attachments)
  const projectAttachments = (cardFiles || []) as FileCardType[];
  const hasProjectAttachments = projectAttachments.length > 0;

  // Vendor-side attachments (voucher_responses.attachments, filtered to this voucher)
  const vendorAttachments = (vendorFiles || []) as FileCardType[];
  const hasVendorAttachments = vendorAttachments.length > 0;

  const [
    showConfirmDeleteAttachmentModal,
    setShowConfirmDeleteAttachmentModal,
  ] = useState(false);
  const [
    attachmentToDelete,
    setAttachmentToDelete,
  ] = useState<FileCardType | null>(null);

  // Drawer state for file preview
  const [previewDrawerVisible, setPreviewDrawerVisible] = useState(false);
  const [previewFile, setPreviewFile] = useState<{
    url: string;
    name: string;
    extension: string;
    dbFile?: FileCardType;
  } | null>(null);
  const [isLoadingUrl, setIsLoadingUrl] = useState(false);

  const { setFileProgressContext, fileProgressContext } = useContext(
    FileProgressContext
  );

  const [doDeleteAttachment, { isLoading: isDeletingAttachment }] = useMutation(
    deleteVoucherAttachment,
    {
      onSuccess: () => {
        message.success(
          intl.formatMessage({ id: 'attachment-delete-successful' })
        );
        queryCache.invalidateQueries([projectId, tenderId, contractId]);
      },
      onError: () => {
        message.error(
          intl.formatMessage({ id: 'attachment-delete-successful' })
        );
      },
    }
  );

  // Vendor can manage their own files (download / delete / upload) only when offer is created
  const canVendorManageFiles =
    isOnMyTender && !!contractDataMyTender.contractOfferData?.is_offer_created;

  // Extract primitives to avoid unstable dependency issues
  const shareId = contractDataMyTender?.shareId;
  const shareKey = contractDataMyTender?.shareKey;
  const offerId = contractDataMyTender?.contractOfferData?.offer_id;

  // Track upload status to prevent infinite loops
  const wasUploadingVendor = useRef(false);
  const wasUploadingProject = useRef(false);

  // refetch contract after upload is done (Project side)
  useEffect(() => {
    if (isOnMyTender || !projectId || !tenderId || !contractId || !id) {
      return;
    }

    const hasPendingUploads = fileProgressContext?.some(
      (f) =>
        f.voucherId === id &&
        f.contractId === contractId &&
        f.tenderId === tenderId &&
        !f.isDone
    );

    if (hasPendingUploads) {
      wasUploadingProject.current = true;
    }

    if (!hasPendingUploads && wasUploadingProject.current) {
      wasUploadingProject.current = false;
      queryCache.invalidateQueries([projectId, tenderId, contractId]);
    }
  }, [fileProgressContext, id, contractId, tenderId, projectId, isOnMyTender]);

  // refetch voucher_responses after vendor upload is done (Vendor side – MyTender)
  useEffect(() => {
    if (!isOnMyTender || !id) return;
    if (!shareId || !shareKey || !offerId) return;

    const hasPendingVendorUploads = fileProgressContext?.some(
      (f) =>
        f.isVendorVoucherResponse && // flag для vendor upload
        f.voucherId === id &&
        f.shareId === shareId &&
        f.shareKey === shareKey &&
        f.offerId === offerId &&
        !f.isDone
    );

    if (hasPendingVendorUploads) {
      wasUploadingVendor.current = true;
    }

    // коли всі vendor-аплоади завершені → оновлюємо voucher_responses
    if (!hasPendingVendorUploads && wasUploadingVendor.current) {
      wasUploadingVendor.current = false;
      queryCache.invalidateQueries([
        'voucher-responses',
        shareId,
        shareKey,
        offerId,
      ]);
      queryCache.invalidateQueries([
        'contracts',
        undefined,
        undefined,
        shareId,
        shareKey,
      ]);
    }
    // Updated dependencies to use primitives
  }, [fileProgressContext, isOnMyTender, id, shareId, shareKey, offerId]);

  // Uploading files to API
  const handleConfirmUpload = (fileList: UploadFile[]) => {
    if (tenderClosed || isOfferLocked || isDeleted || contractDeleted) return;

    // Vendor side (MyTender) – TUS via tender_shares/.../responses/attachment/tus
    if (isOnMyTender) {
      if (!shareId || !shareKey || !offerId) {
        message.error(
          intl.formatMessage({ id: 'missing-share-offer-data-upload' })
        );
        return;
      }

      setFileProgressContext((prev) => [
        ...(prev || []),
        ...fileList.map(
          (file) =>
            //@ts-ignore
            ({
              id: uuid(),
              file: file.originFileObj!,
              shouldStartUpload: true,
              isDone: false,
              voucherId: id,
              entityCreated: false,
              commit: undefined,
              progress: 0,
              // VENDOR-FLOW
              shareId,
              shareKey,
              offerId,
              isVendorVoucherResponse: true,
            } as FileProgress)
        ),
      ]);

      setShowUploadModal(false);
      return;
    }

    // Project side upload (voucher.attachments)
    setFileProgressContext((prev) => [
      ...prev,
      ...fileList.map(
        (file) =>
          //@ts-ignore
          ({
            id: uuid(),
            file: file.originFileObj!,
            shouldStartUpload: true,
            isDone: false,
            projectId: projectId,
            tenderId: tenderId,
            contractId: contractId,
            voucherId: id,
            entityCreated: false,
            commit: undefined,
            progress: 0,
          } as FileProgress)
      ),
    ]);

    setShowUploadModal(false);
  };

  // Confirm Delete Attachment Modal
  const handleConfirmDeleteAttachment = async () => {
    if (tenderClosed || isOfferLocked || isDeleted || contractDeleted) return;
    if (!attachmentToDelete) return;

    try {
      if (isOnMyTender) {
        // Vendor can delete only their own response file(s)
        if (!shareId || !shareKey || !offerId) {
          message.error(
            intl.formatMessage({ id: 'missing-share-offer-data-delete' })
          );
          return;
        }

        await deleteVendorVoucherAttachment({
          shareId,
          shareKey,
          offerId,
          voucherId: id,
          fileGuid: attachmentToDelete.id,
        });

        message.success(
          intl.formatMessage({ id: 'attachment-delete-successful' })
        );

        // refetch to update vendor responses list
        queryCache.invalidateQueries([
          'voucher-responses',
          shareId,
          shareKey,
          offerId,
        ]);
        queryCache.invalidateQueries([
          'contracts',
          undefined,
          undefined,
          shareId,
          shareKey,
        ]);
      } else {
        // Project deletes only its own voucher.attachments
        await doDeleteAttachment({
          projectId,
          tenderId,
          contractId,
          voucherId: id,
          fileGuid: attachmentToDelete.id,
        });
      }
    } finally {
      setAttachmentToDelete(null);
      setShowConfirmDeleteAttachmentModal(false);
    }
  };

  const handleOpenUploadModal = () => {
    if (tenderClosed || isOfferLocked || isDeleted || contractDeleted) return;
    setShowUploadModal(true);
  };

  // Context Menu (Project side only – used for adding Project attachment)
  const handleContextMenuClick = (item: DataContextMenuItemType) => {
    if (tenderClosed || isOfferLocked || contractDeleted) return;

    switch (item.modal) {
      case 'delete':
        onCardAction?.('delete');
        break;
      case 'reactivate-card':
        onCardAction?.('reactivate-card');
        break;
      case 'edit-card':
        onCardAction?.('edit-card');
        break;
      case 'add-file':
        // Only allow adding project attachment if none exists
        if (!hasProjectAttachments && !isDeleted) {
          handleOpenUploadModal();
        }
        break;
    }
  };

  // DnD
  const cardRef = useRef<HTMLDivElement>(null);
  useDraggableCard(cardRef, id, {
    disabled:
      isOnMyTender ||
      tenderClosed ||
      isOfferLocked ||
      !!isDeleted ||
      !!contractDeleted,
    onDragStart: () => {
      if (!isOnMyTender) setDraggingCardId?.(id);
    },
    onDrop: () => {
      if (!isOnMyTender) setDraggingCardId?.(null);
    },
  });

  // ToolTip Text
  const dragDropTooltipTitle =
    !isOnMyTender &&
    intl.formatMessage({
      id: 'drag-and-drop-to-reorder',
    });

  const RightClickWrapper = !isOnMyTender ? BaseRightClickOverlay : Fragment;
  const wrapperProps = !isOnMyTender
    ? {
        items: (isDeleted
          ? dataContextMenu.deletedContractCard
          : dataContextMenu.contractCard
        ).map((item) => ({
          ...item,
          disabled:
            !isOnMyTender && (tenderClosed || isOfferLocked || contractDeleted)
              ? true
              : item.disabled,
        })),
        onItemClick: handleContextMenuClick,
        onOverlayClose: () => {
          //
        },
      }
    : {};
  const formatFilename = (name: string, maxLength: number) => {
    const lastDot = name.lastIndexOf('.');

    if (lastDot === -1) {
      // no extension
      if (name.length <= maxLength) return name;
      return name.slice(0, maxLength) + '…';
    }

    const base = name.slice(0, lastDot);
    const ext = name.slice(lastDot);
    if (name.length <= maxLength) return name;

    // Leave extension visible, shorten only the base
    const shortenedBase = base.slice(0, maxLength - ext.length - 1);

    return shortenedBase + '…' + ext;
  };

  // Helper functions for drawer preview
  const isPreviewable = (extension: string) => {
    const ext = extension.toLowerCase();
    const isPdf = /^pdf$/i.test(ext);
    const isImage = /^(jpg|jpeg|png|gif|webp)$/i.test(ext);
    return isPdf || isImage;
  };

  const handleDrawerClose = () => {
    setPreviewDrawerVisible(false);
    setPreviewFile(null);
  };

  const handleFileDownload = () => {
    if (previewFile?.url) {
      const link = document.createElement('a');
      link.href = previewFile.url;
      link.download = previewFile.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleAttachmentPreview = async (
    file: FileCardType,
    source: 'project' | 'vendor'
  ) => {
    const extension = file.name.split('.').pop() || '';
    if (!isPreviewable(extension)) return;

    // Open drawer immediately with empty URL (shows loading spinner)
    setPreviewFile({ url: '', name: file.name, extension, dbFile: file });
    setPreviewDrawerVisible(true);
    setIsLoadingUrl(true);

    try {
      let url: string;

      if (source === 'vendor') {
        // Vendor side
        if (!shareId || !shareKey || !offerId) {
          message.error(
            intl.formatMessage({
              id: 'missing-share-offer-data-download',
            })
          );
          setPreviewDrawerVisible(false);
          setPreviewFile(null);
          return;
        }

        url = await getVendorVoucherAttachmentDownloadUrl({
          shareId,
          shareKey,
          offerId,
          voucherId: id,
          fileGuid: file.id,
        });
      } else {
        // Project side
        if (isOnMyTender) {
          // Project file viewed from vendor side
          const contractId =
            contractDataMyTender?.contractOfferData?.contract_id;

          if (!shareId || !shareKey || !contractId) {
            message.error(
              intl.formatMessage({
                id: 'missing-contract-data-download',
              })
            );
            setPreviewDrawerVisible(false);
            setPreviewFile(null);
            return;
          }

          url = await getProjectVoucherAttachmentDownloadUrl({
            shareId,
            shareKey,
            contractId,
            voucherId: id,
            fileGuid: file.id,
          });
        } else {
          // Project file viewed from project side
          url = await getVoucherAttachmentDownloadUrl({
            projectId,
            tenderId,
            contractId,
            voucherId: id,
            fileGuid: file.id,
          });
        }
      }

      // Update with actual URL - Viewer will re-render with content
      setPreviewFile({ url, name: file.name, extension, dbFile: file });
    } catch (err) {
      message.error(
        intl.formatMessage({
          id: 'attachment-download-failed',
        })
      );
      setPreviewDrawerVisible(false);
      setPreviewFile(null);
    } finally {
      setIsLoadingUrl(false);
    }
  };

  const renderAttachmentRow = (
    file: FileCardType,
    canDownload: boolean,
    canDelete: boolean,
    source: 'project' | 'vendor'
  ) => {
    const maxNameLength = isOnMyTender ? 13 : 33;
    const displayName = formatFilename(file.name, maxNameLength);

    const isPdf = file.type === 'application/pdf' || file.name.endsWith('.pdf');
    const isXls =
      file.type === 'application/xlsx' ||
      file.type === 'application/xls' ||
      file.name.endsWith('.xlsx') ||
      file.name.endsWith('.xls');

    const finalCanDownload = canDownload;
    // && !tenderClosed; TV1-511
    const finalCanDelete = canDelete;

    return (
      <div
        key={file.id}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '3px',
          justifyContent: isOnMyTender ? 'space-between' : 'flex-start',
          padding: '5px 15px 5px 5px',
        }}>
        {isPdf ? (
          <IconButton
            icon={'file-pdf'}
            iconStyle={'fad'}
            color={'red'}
            backgroundOff
            customStyle={{ fontSize: '18px' }}
            containerStyle={{
              maxHeight: '18px',
              maxWidth: '18px',
              minHeight: '18px',
              minWidth: '18px',
            }}
          />
        ) : isXls ? (
          <IconButton
            icon={'file-spreadsheet'}
            iconStyle={'fad'}
            color={'green'}
            backgroundOff
            customStyle={{ fontSize: '18px' }}
            containerStyle={{
              maxHeight: '18px',
              maxWidth: '18px',
              minHeight: '18px',
              minWidth: '18px',
            }}
          />
        ) : (
          // @ts-ignore
          <PaperClipOutlined style={{ fontSize: '18px' }} />
        )}

        <span style={{ fontWeight: 'lighter', fontSize: '14px' }}>
          {displayName}
        </span>

        <div>
          {/* Preview icon for previewable files */}
          {isPreviewable(file.name.split('.').pop() || '') && (
            <IconButton
              icon={'eye'}
              iconStyle={'far'}
              color={'red'}
              customStyle={{ fontSize: '16px' }}
              click={() => handleAttachmentPreview(file, source)}
            />
          )}

          {finalCanDownload && (
            <IconButton
              icon={'download'}
              iconStyle={'far'}
              color={'blue'}
              click={async () => {
                // if (tenderClosed) return; TV1-511

                try {
                  let url: string;

                  if (isOnMyTender) {
                    // --- VENDOR SIDE
                    const shareId = contractDataMyTender?.shareId;
                    const shareKey = contractDataMyTender?.shareKey;
                    const offerId =
                      contractDataMyTender?.contractOfferData?.offer_id;
                    const contractId =
                      contractDataMyTender?.contractOfferData?.contract_id;

                    if (!shareId || !shareKey) {
                      message.error(
                        intl.formatMessage({
                          id: 'missing-share-data-download',
                        })
                      );
                      return;
                    }

                    if (canDelete) {
                      // canDelete === true => це vendor-файл (можемо його видаляти)
                      if (!offerId) {
                        message.error(
                          intl.formatMessage({
                            id: 'missing-offer-data-download',
                          })
                        );
                        return;
                      }

                      url = await getVendorVoucherAttachmentDownloadUrl({
                        shareId,
                        shareKey,
                        offerId,
                        voucherId: id,
                        fileGuid: file.id,
                      });
                    } else {
                      // canDelete === false => PROJECT-file from VENDOR side
                      if (!contractId) {
                        message.error(
                          intl.formatMessage({
                            id: 'missing-contract-data-download',
                          })
                        );
                        return;
                      }

                      url = await getProjectVoucherAttachmentDownloadUrl({
                        shareId,
                        shareKey,
                        contractId,
                        voucherId: id,
                        fileGuid: file.id,
                      });
                    }
                  } else {
                    // --- PROJECT SIDE ---
                    url = await getVoucherAttachmentDownloadUrl({
                      projectId,
                      tenderId,
                      contractId,
                      voucherId: id,
                      fileGuid: file.id,
                    });
                  }

                  window.open(url, '_blank');
                } catch (err) {
                  message.error(
                    intl.formatMessage({
                      id: 'attachment-download-failed',
                    })
                  );
                }
              }}
              customStyle={{ fontSize: '16px' }}
            />
          )}

          {finalCanDelete && (
            <IconButton
              icon={'trash'}
              iconStyle={'far'}
              color={
                tenderClosed || isOfferLocked || isDeleted || contractDeleted
                  ? 'grey'
                  : 'red'
              }
              click={() => {
                if (
                  tenderClosed ||
                  isOfferLocked ||
                  isDeleted ||
                  contractDeleted
                )
                  return;
                setAttachmentToDelete(file);
                setShowConfirmDeleteAttachmentModal(true);
              }}
              customStyle={{
                fontSize: '16px',
                cursor:
                  tenderClosed || isOfferLocked || isDeleted || contractDeleted
                    ? 'not-allowed'
                    : 'pointer',
                opacity:
                  tenderClosed || isOfferLocked || isDeleted || contractDeleted
                    ? 0.5
                    : 1,
              }}
            />
          )}
        </div>
      </div>
    );
  };

  return (
    <Fragment>
      <RightClickWrapper {...(wrapperProps as any)}>
        <div
          ref={cardRef}
          css={[
            styles.cardWrapper,
            borderless && { border: 'none' },
            isDragging && styles.cardGhost,
            isDeleted && {
              backgroundColor: '#fff2f0',
              border: '1px solid #ffccc7',
              opacity: 0.88,
            },
          ]}
          onContextMenu={onContextMenu}>
          {/* HEADER */}
          {cardTitle &&
            (isOnMyTender ? (
              <div
                css={{
                  display: 'flex',
                  flex: '5',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}>
                <div css={{ width: '100%' }}>
                  <div
                    css={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      flexWrap: 'wrap',
                    }}>
                    {isOptional && renderOptionalVoucherBadge(intl)}

                    <div css={[styles.cardTitle, { fontSize: '16px' }]}>
                      {cardTitle}
                    </div>
                  </div>
                  {cardSubtitle && (
                    <div
                      css={{
                        marginTop: 4,
                        color: 'gray',
                        fontSize: '14px',
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word',
                      }}>
                      {cardSubtitle}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div css={styles.cardTitleWrapper}>
                <div
                  css={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {isOptional && renderOptionalVoucherBadge(intl)}

                  <div css={{ display: 'table', width: 'auto' }}>
                    <Tooltip
                      title={dragDropTooltipTitle}
                      placement="top"
                      mouseEnterDelay={0.5}
                      trigger={['hover']}>
                      <div
                        css={[
                          styles.cardTitle,
                          !cardSubtitle && { fontSize: '20px' },
                          { display: 'inline-block', width: 'auto' },
                        ]}>
                        {cardTitle}
                      </div>
                    </Tooltip>
                  </div>
                  {/* Project side Type Icon */}
                  {renderVoucherTypeIcon(subType, intl)}
                </div>
                <div
                  css={styles.cardSubTitle}
                  style={{
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word',
                  }}>
                  {cardSubtitle}
                </div>
              </div>
            ))}

          {/* ATTACHMENTS */}
          <div css={styles.cardFileWrapper}>
            {/* Project column */}
            <div
              css={{
                flex: 4,
                paddingRight: isOnMyTender ? 8 : 0,
                borderRight: isOnMyTender
                  ? '1px solid rgba(0,0,0,0.06)'
                  : 'none',
              }}>
              <div
                css={{
                  fontSize: '12px',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  marginBottom: 4,
                  color: 'rgba(0,0,0,0.45)',
                }}>
                {intl.formatMessage({ id: 'project-file' }, {}) ||
                  'Project file'}
              </div>

              {projectAttachments.length === 0 && (
                <span
                  style={{
                    color: 'grey',
                    fontSize: '14px',
                    fontWeight: 'lighter',
                  }}>
                  {intl.formatMessage({ id: 'no-attachment-available' })}
                </span>
              )}

              {projectAttachments.map((file) =>
                renderAttachmentRow(
                  file,
                  /* canDownload */ !isOnMyTender || canVendorManageFiles,
                  /* canDelete */ !isOnMyTender, // на Vendor side проектні файли не видаляємо
                  /* source */ 'project'
                )
              )}
            </div>

            {/* Vendor column – only on Vendor side */}
            {isOnMyTender && (
              <div
                css={{
                  flex: 4,
                  paddingLeft: 8,
                  textAlign: 'right',
                }}>
                <div
                  css={{
                    fontSize: '12px',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    marginBottom: 4,
                    color: 'rgba(0,0,0,0.45)',
                    padding:
                      vendorAttachments.length === 0 ? '0' : '0px 15px 0px 0px',
                  }}>
                  {intl.formatMessage({ id: 'vendor-file' }, {}) ||
                    'Vendor file'}
                </div>

                {vendorAttachments.length === 0 && (
                  <span
                    style={{
                      color: 'grey',
                      fontSize: '14px',
                      fontWeight: 'lighter',
                      display: 'block',
                    }}>
                    {intl.formatMessage({ id: 'no-attachment-available' })}
                  </span>
                )}

                {vendorAttachments.map((file) =>
                  renderAttachmentRow(
                    file,
                    /* canDownload */ canVendorManageFiles,
                    /* canDelete */ canVendorManageFiles,
                    /* source */ 'vendor'
                  )
                )}
              </div>
            )}
          </div>

          {/* Upload Buttons */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {/* Project side upload (left column) */}
            {!isOnMyTender && (
              <Button
                css={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                onClick={handleOpenUploadModal}
                disabled={
                  hasProjectAttachments ||
                  !!tenderClosed ||
                  !!isOfferLocked ||
                  !!isDeleted ||
                  !!contractDeleted
                }>
                <IconButton
                  icon={'up-from-bracket'}
                  iconStyle={'far'}
                  backgroundOff
                  customStyle={{ fontSize: '16px' }}
                />
                <span>{intl.formatMessage({ id: 'upload-str' })}</span>
              </Button>
            )}

            {/* Vendor side upload (right column – answer file) */}
            {isOnMyTender && canVendorManageFiles && (
              <Button
                css={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                onClick={handleOpenUploadModal}
                disabled={
                  hasVendorAttachments ||
                  !!tenderClosed ||
                  !!isOfferLocked ||
                  !!isDeleted ||
                  !!contractDeleted
                }>
                <IconButton
                  icon={'up-from-bracket'}
                  iconStyle={'far'}
                  backgroundOff
                  customStyle={{ fontSize: '16px' }}
                />
                <span>{intl.formatMessage({ id: 'upload-str' })}</span>
              </Button>
            )}
          </div>

          {/* (Vendor) – date + type*/}
          {isOnMyTender && (
            <div
              css={{
                textAlign: 'right',
                color: 'gray',
                fontSize: '12px',
                marginTop: '8px',
              }}>
              <div>
                {/* {moment.unix(cardCreationDate).format('DD/MM/YYYY - HH:mm')} TV1-513 */}
              </div>
              <div style={{ marginTop: '4px' }}>
                {renderVoucherTypeIcon(subType, intl)}
              </div>
            </div>
          )}

          {/* Kebab – тільки Project */}
          {!isOnMyTender && (
            <div css={styles.cardOptionWrapper}>
              <IconButton
                icon={['far', 'ellipsis-v']}
                widthTooltip={intl.formatMessage({ id: 'settings' })}
                customStyle={{
                  cursor:
                    !isOnMyTender &&
                    (tenderClosed || isOfferLocked || contractDeleted)
                      ? 'not-allowed'
                      : 'pointer',
                  opacity:
                    !isOnMyTender &&
                    (tenderClosed || isOfferLocked || contractDeleted)
                      ? 0.5
                      : 1,
                }}
                click={(e) => {
                  onRightClick?.(e);
                  const fakeEvent = new MouseEvent('contextmenu', {
                    bubbles: true,
                    cancelable: true,
                    view: window,
                    button: 2,
                    buttons: 2,
                    clientX: e.clientX,
                    clientY: e.clientY,
                  });
                  e.currentTarget.dispatchEvent(fakeEvent);
                }}
              />
            </div>
          )}
        </div>
      </RightClickWrapper>

      {/* MODALS */}
      <UploadFileModal
        maxFiles={1}
        visible={showUploadModal}
        beforeUpload={() => false}
        action=""
        onCancel={() => {
          setShowUploadModal(false);
        }}
        onConfirm={handleConfirmUpload}
      />
      <SimpleConfirmModal
        visible={showConfirmDeleteAttachmentModal}
        isLoading={isDeletingAttachment}
        okButtonProps={{
          disabled:
            !!tenderClosed ||
            !!isOfferLocked ||
            !!isDeleted ||
            !!contractDeleted,
        }}
        onOk={handleConfirmDeleteAttachment}
        onCancel={() => {
          setAttachmentToDelete(null);
          setShowConfirmDeleteAttachmentModal(false);
        }}
        modalTitle={`${intl.formatMessage({ id: 'delete' })} - ${
          attachmentToDelete?.name
        }`}
        modalIcon={['far', 'trash']}
        okText={intl.formatMessage({ id: 'delete' })}
        okType="danger"
      />

      {/* Drawer preview */}
      {previewFile && (
        <Viewer
          mode="drawer"
          drawerVisible={previewDrawerVisible}
          onDrawerClose={handleDrawerClose}
          drawerTitle={previewFile.name}
          drawerWidth={800}
          fileUrl={previewFile.url}
          fileExtension={previewFile.extension}
          showDownloadButton={true}
          onDownload={handleFileDownload}
          isLoadingUrl={isLoadingUrl}
        />
      )}
    </Fragment>
  );
};

export default OfferListCard;
